/**
 * usePodBeneficiary — commits POD (Payable on Death) beneficiary changes on
 * a deposit account (DDA/SAV/CD).
 *
 * Legacy procedure references:
 *   - `No POD – Release of Funds.pdf`
 *   - `POD - Health Net Release.pdf`
 *   - `Beneficiary Passed.pdf`
 *
 * Flow per entry:
 *   - For Add (primary): CustRelAdd with RelCode='BP' (BC for contingent)
 *   - For Remove: AcctMod with Dlt=true targeting the CustRel record
 *
 * Emits session events per entry so the audit trail captures every BP/BC
 * addition/removal.
 */

import { useCallback } from 'react';
import type { CustRelAddRequest, AcctModRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useApi } from '../../hooks/useApi';
import { useEmitEvent } from '../../hooks/useEmitEvent';
import type { MaintActionResult } from '../types';

export interface PodAddEntry {
  op: 'add';
  role: 'primary' | 'contingent';
  /** Existing CIF id of the beneficiary, if one was selected. */
  beneficiaryCifId: string;
  percentage: number;
}

export interface PodRemoveEntry {
  op: 'remove';
  relRecordId: string;
  role: 'primary' | 'contingent';
}

export type PodChangeEntry = PodAddEntry | PodRemoveEntry;

export interface PodBeneficiaryInput {
  cifId: string;
  acctId: string;
  acctType: AcctType | string;
  officerCode: string;
  entries: ReadonlyArray<PodChangeEntry>;
}

export interface UsePodBeneficiaryApi {
  run: (input: PodBeneficiaryInput) => Promise<MaintActionResult>;
}

export function usePodBeneficiary(): UsePodBeneficiaryApi {
  const { client } = useApi();
  const emit = useEmitEvent();

  const run = useCallback(
    async (input: PodBeneficiaryInput): Promise<MaintActionResult> => {
      if (input.entries.length === 0) {
        return { ok: false, error: 'No changes to save.' };
      }

      let succeeded = 0;
      const errors: string[] = [];

      for (const entry of input.entries) {
        try {
          if (entry.op === 'add') {
            const req: CustRelAddRequest = {
              primaryCifId: input.cifId,
              jointCifId: entry.beneficiaryCifId,
              acctId: input.acctId,
              acctType: input.acctType as AcctType,
              custRelCode: 'POD',
              acctRelCode: entry.role === 'primary' ? 'BP' : 'BC',
              fdicCat: 'TR',
              ...(typeof entry.percentage === 'number' ? { podPercentage: entry.percentage } : {}),
              offCode: input.officerCode,
              operatorNumber: input.officerCode,
            };
            await client.accounts.custRelAdd(req);
            emit({
              eventType: 'beneficiary.added',
              eventCategory: 'cif-account',
              payload: {
                cifId: input.cifId,
                acctId: input.acctId,
                beneficiaryCifId: entry.beneficiaryCifId,
                role: entry.role,
                percentage: entry.percentage,
              },
            });
            succeeded += 1;
          } else {
            const req: AcctModRequest = {
              AcctId: input.acctId,
              AcctType: input.acctType as AcctType,
              Dlt: true,
              Fields: { RelRecordId: entry.relRecordId },
              OperatorNumber: input.officerCode,
            };
            await client.accounts.acctMod(req);
            emit({
              eventType: 'beneficiary.removed',
              eventCategory: 'cif-account',
              payload: {
                cifId: input.cifId,
                acctId: input.acctId,
                relRecordId: entry.relRecordId,
                role: entry.role,
              },
            });
            succeeded += 1;
          }
        } catch (err) {
          errors.push(err instanceof Error ? err.message : String(err));
        }
      }

      // Auto-create a signature-card task whenever any BP/BC change landed.
      if (succeeded > 0) {
        try {
          await client.retail.tasks.create({
            title: 'Signature card update — POD beneficiary change',
            type: 'signature-card-update',
            category: 'workflow',
            priority: 'Normal',
            assignedTo: input.officerCode,
            cifId: input.cifId,
            severity: 'info',
            autoCreated: true,
            description:
              `Account ${input.acctId} beneficiaries changed — reprint + customer ` +
              `re-sign required. See: No POD – Release of Funds procedure.`,
          });
        } catch {
          /* non-fatal */
        }
      }

      // TODO: wire disclosure packet when Team 2's engine lands
      //       (Beneficiary Designation form).

      if (succeeded === 0) {
        return {
          ok: false,
          error: errors[0] ?? 'All beneficiary operations failed.',
        };
      }

      return {
        ok: true,
        message:
          `${succeeded} of ${input.entries.length} beneficiary operation(s) committed.` +
          (errors.length > 0 ? ` ${errors.length} failed — see log.` : ''),
      };
    },
    [client, emit],
  );

  return { run };
}
